import classnames from 'classnames';
import React, { Ref } from 'react';
import mergeRefs from 'react-merge-refs';

import { ErrorLabel } from '@eika/label';

import { FocusableHtmlElement, useFocusRef } from '../hooks';
import { updateKredittkortnummer } from '../reducer/sporsmalsflyt-actions';
import { useSporsmalsflytDispatch } from '../reducer/sporsmalsflytDispatchProvider';
import {
    CurrentKredittkortnummerSporsmal,
    InputGroup,
    inputGroup,
    PARTIAL_KREDITTKORTNUMMER_ERROR,
    validatePartialKredittkortnummer,
} from './kredittkortnummer-types';

import './kredittkortnummer.scss';

interface KredittkortnummerProps {
    currentSporsmal: CurrentKredittkortnummerSporsmal;
    showFeilmelding: boolean;
    focusOnMount: boolean;
    errorRef?: Ref<FocusableHtmlElement>;
}

export const Kredittkortnummer = ({
    currentSporsmal,
    showFeilmelding,
    focusOnMount,
    errorRef,
}: KredittkortnummerProps) => {
    const textField = useFocusRef<HTMLInputElement & HTMLTextAreaElement>(focusOnMount);
    const dispatch = useSporsmalsflytDispatch();
    const kredittkortnummerGroups = currentSporsmal.kredittkortnummer.replace(/\*/g, '').split('-');
    const error = showFeilmelding && !validatePartialKredittkortnummer(currentSporsmal.kredittkortnummer);
    const { id, tekst, ingress } = currentSporsmal.sporsmal;

    const onChange = (value: string, inputGroup: InputGroup) => dispatch(updateKredittkortnummer(value, inputGroup));

    return (
        <div className="container__standard-width">
            <fieldset className="kredittkortnummer">
                <legend className={classnames('sgw-input__input-mainlabel', { 'with-sublabel': ingress })}>
                    <span className="sgw-radio-button-legend-text">{tekst}</span>
                </legend>
                {ingress && <p className="sgw-input__input-sublabel">{ingress}</p>}
                <div className="kredittkortnummer__input-container">
                    <div className="kredittkortnummer__input-wrapper">
                        <label className="screen-reader-text" htmlFor={`kredittkortnummer-sporsmal-${id}-group-1`}>
                            Siffer 1-4
                        </label>
                        <input
                            className={classnames('sgw-input__input', { 'sgw-input__error': error })}
                            aria-invalid={error}
                            id={`kredittkortnummer-sporsmal-${id}-group-1`}
                            type="number"
                            inputMode="numeric"
                            value={kredittkortnummerGroups[inputGroup.one.index] || ''}
                            onInput={(event) => onChange(event.currentTarget.value, inputGroup.one)}
                            ref={mergeRefs([textField, errorRef])}
                            placeholder="1234"
                            maxLength={4}
                        />
                    </div>
                    <label className="screen-reader-text" htmlFor={`kredittkortnummer-sporsmal-${id}-group-2`}>
                        Siffer 5-6
                    </label>
                    <div className="kredittkortnummer__input-wrapper">
                        <input
                            className={classnames('sgw-input__input', { 'sgw-input__error': error })}
                            aria-invalid={error}
                            id={`kredittkortnummer-sporsmal-${id}-group-2`}
                            type="number"
                            inputMode="numeric"
                            value={kredittkortnummerGroups[inputGroup.two.index] || ''}
                            onInput={(event) => onChange(event.currentTarget.value, inputGroup.two)}
                            placeholder="56**"
                            maxLength={2}
                        />
                        {kredittkortnummerGroups[inputGroup.two.index]?.length === 2 && (
                            <span className="kredittkortnummer__input-wrapper--overlay">**</span>
                        )}
                    </div>
                    <div className="kredittkortnummer__input-wrapper kredittkortnummer__input-wrapper__dummy">
                        <div
                            className={classnames('sgw-input__input', 'kredittkortnummer__input', {
                                'sgw-input__error': error,
                            })}
                        >
                            ****
                        </div>
                    </div>
                    <label className="screen-reader-text" htmlFor={`kredittkortnummer-sporsmal-${id}-group-3`}>
                        Siffer 13-16
                    </label>
                    <div className="kredittkortnummer__input-wrapper">
                        <input
                            className={classnames('sgw-input__input', { 'sgw-input__error': error })}
                            aria-invalid={error}
                            id={`kredittkortnummer-sporsmal-${id}-group-3`}
                            type="number"
                            inputMode="numeric"
                            value={kredittkortnummerGroups[inputGroup.three.index] || ''}
                            onInput={(event) => onChange(event.currentTarget.value, inputGroup.three)}
                            placeholder="7890"
                            maxLength={4}
                        />
                    </div>
                </div>
                {error && (
                    <ErrorLabel
                        aria-live="polite"
                        className="sgw-input__error-label"
                        text={PARTIAL_KREDITTKORTNUMMER_ERROR}
                    />
                )}
            </fieldset>
        </div>
    );
};
